import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Heart, MapPin, ArrowRight, ArrowUpRight, Sparkles, Building2,
  BarChart3, ChevronLeft, ChevronRight, Star, X, Check, Home, Menu, XCircle
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { motion, AnimatePresence } from "framer-motion";
import { getFeaturedPartners } from "@/constants/masterPartners";

const BG_ABSTRACT = "https://static.prod-images.emergentagent.com/jobs/8030f0c6-1868-478d-bdbe-db9d69a11e86/images/ae1b88dfbb4dda41ae482974d3747aa7b9b16efc3439956e0bfb621bd9d087ef.png";
const PROPERTY_1 = "https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";
const PROPERTY_2 = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

const PILLARS = [
  { name: "Emocional",  weight: 35, icon: Heart,       desc: "Cómo se siente vivir ahí" },
  { name: "Técnico",    weight: 25, icon: Building2,   desc: "Distribución y especificaciones" },
  { name: "Urbano",     weight: 20, icon: MapPin,      desc: "Ubicación y entorno" },
  { name: "Financiero", weight: 20, icon: BarChart3,   desc: "Precio y plusvalía" },
];

const DEMO_PROPERTIES = [
  { id: 1, title: "Penthouse en Polanco", location: "Polanco, CDMX",  price: "$15M MXN", score: 94, img: PROPERTY_1, tags: ["Luz Natural", "Home Office", "Pet Friendly"] },
  { id: 2, title: "Loft de Diseño",       location: "Condesa, CDMX",  price: "$12M MXN", score: 87, img: PROPERTY_2, tags: ["Terraza", "Wow Factor"] },
  { id: 3, title: "Casa con Jardín",      location: "Coyoacán, CDMX", price: "$9.5M MXN", score: 82, img: PROPERTY_1, tags: ["Naturaleza", "Mascotas"] },
];

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative py-24 md:py-32 px-6 lg:px-8 ${className}`}>
    <div className="max-w-7xl mx-auto">{children}</div>
  </section>
);

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#CCD500]">
    <span className="h-px w-8 bg-[#CCD500]/60" /> {children}
  </span>
);

const SwipeDemo = () => {
  const [idx, setIdx] = useState(0);
  const [match, setMatch] = useState(false);
  const handle = (dir) => {
    if (dir === "right" && DEMO_PROPERTIES[idx].score >= 80) {
      setMatch(true);
      setTimeout(() => { setMatch(false); setIdx((p) => (p + 1) % DEMO_PROPERTIES.length); }, 1600);
    } else { setIdx((p) => (p + 1) % DEMO_PROPERTIES.length); }
  };
  const prop = DEMO_PROPERTIES[idx];
  return (
    <div className="relative w-full max-w-[320px] mx-auto" data-testid="swipe-demo">
      <div className="absolute -inset-6 rounded-[48px] bg-gradient-to-br from-[#CCD500]/30 via-transparent to-transparent blur-2xl" />
      <div className="relative bg-slate-900 rounded-[40px] p-2 shadow-[0_30px_80px_-20px_rgba(204,213,0,0.25)] border border-white/10">
        <div className="relative h-[520px] rounded-[32px] overflow-hidden bg-slate-950">
          <AnimatePresence mode="wait">
            <motion.div key={prop.id} initial={{ opacity: 0, scale: 0.95, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
              exit={{ x: 200, opacity: 0, rotate: 8 }} transition={{ duration: 0.35 }} className="absolute inset-0">
              <img src={prop.img} alt={prop.title} className="w-full h-2/3 object-cover" />
              <div className="absolute top-4 right-4 bg-[#CCD500] text-black px-3 py-1.5 rounded-full font-bold text-sm flex items-center gap-1.5 shadow-lg">
                <Sparkles className="w-4 h-4" /> {prop.score}%
              </div>
              <div className="absolute inset-0 top-2/3 bg-gradient-to-t from-slate-950 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
                <h4 className="font-display text-xl font-bold mb-1">{prop.title}</h4>
                <p className="text-sm text-slate-300 flex items-center gap-1.5 mb-3">
                  <MapPin className="w-3.5 h-3.5" /> {prop.location} • {prop.price}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {prop.tags.map((t) => (<span key={t} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-white/10 border border-white/10">{t}</span>))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
          <AnimatePresence>
            {match && (
              <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.6 }}
                className="absolute inset-0 bg-[#CCD500]/95 flex flex-col items-center justify-center z-30">
                <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 0.7 }}>
                  <Heart className="w-20 h-20 text-black fill-black" />
                </motion.div>
                <h3 className="font-display text-3xl font-black text-black mt-3">¡Es un Match!</h3>
                <p className="text-black/80 mt-1 font-medium">{prop.score}% compatible</p>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-5 z-10">
            <button onClick={() => handle("left")} data-testid="swipe-pass-btn"
              className="w-14 h-14 bg-white/10 backdrop-blur-md border border-white/20 rounded-full flex items-center justify-center hover:bg-red-500/20 hover:border-red-400 transition-all hover:scale-110">
              <X className="w-6 h-6 text-white" />
            </button>
            <button onClick={() => handle("right")} data-testid="swipe-like-btn"
              className="w-14 h-14 bg-[#CCD500] rounded-full flex items-center justify-center hover:scale-110 transition-all shadow-[0_0_30px_rgba(204,213,0,0.5)]">
              <Heart className="w-6 h-6 text-black fill-black" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MasterPartnerCard = ({ p, onClick }) => (
  <motion.div whileHover={{ y: -6 }} onClick={onClick}
    className="relative shrink-0 w-[320px] rounded-3xl overflow-hidden bg-slate-900/60 backdrop-blur-xl border border-white/10 hover:border-[#CCD500]/40 transition-colors group cursor-pointer"
    data-testid={`mp-card-${p.slug}`}>
    <div className="relative h-44 overflow-hidden">
      <img src={p.img} alt={p.city} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent" />
      <div className="absolute top-3 left-3">
        <span className="text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 rounded-full bg-[#CCD500] text-black">{p.tag}</span>
      </div>
      <div className="absolute bottom-3 left-4">
        <p className="text-xs text-slate-300 uppercase tracking-wider">{p.city}</p>
        <h4 className="font-display text-2xl font-bold text-white leading-tight">{p.mp}</h4>
      </div>
    </div>
    <div className="p-4 flex items-center justify-between">
      <div className="flex -space-x-2">
        <img src={PROPERTY_1} alt="" className="w-10 h-10 rounded-lg border-2 border-slate-900 object-cover" />
        <img src={PROPERTY_2} alt="" className="w-10 h-10 rounded-lg border-2 border-slate-900 object-cover" />
        <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-slate-800 flex items-center justify-center text-[10px] font-bold text-[#CCD500]">+{p.props}</div>
      </div>
      <span className="text-xs font-semibold text-[#CCD500] flex items-center gap-1 group-hover:gap-2 transition-all">
        Explorar <ArrowUpRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </motion.div>
);

const MasterPartnersCarousel = () => {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const featured = getFeaturedPartners();
  const scrollBy = (dx) => scrollRef.current && scrollRef.current.scrollBy({ left: dx, behavior: "smooth" });
  return (
    <div className="relative">
      <div ref={scrollRef} className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: "none" }} data-testid="mp-carousel">
        <style>{`.scrollbar-hide::-webkit-scrollbar{display:none;}`}</style>
        {featured.map((p) => (
          <div key={p.slug} className="snap-start">
            <MasterPartnerCard p={p} onClick={() => navigate(`/partner/${p.slug}`)} />
          </div>
        ))}
      </div>
      <div className="hidden md:flex absolute -top-16 right-0 gap-2">
        <button onClick={() => scrollBy(-360)} data-testid="mp-carousel-prev"
          className="w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-[#CCD500] hover:text-black text-white flex items-center justify-center transition-all">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button onClick={() => scrollBy(360)} data-testid="mp-carousel-next"
          className="w-11 h-11 rounded-full bg-white/5 border border-white/10 hover:bg-[#CCD500] hover:text-black text-white flex items-center justify-center transition-all">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Top nav menu items
const MENU_ITEMS = [
  { label: "Cómo funciona",                  type: "scroll", target: "#match",    testid: "nav-how" },
  { label: "Haz Match con tu casa ideal",    type: "route",  target: "/haz-match", testid: "nav-haz-match" },
  { label: "Vende tu Propiedad",             type: "route",  target: "/vende-tu-propiedad", testid: "nav-sell" },
  { label: "Conviértete en Agente Findperty", type: "route", target: "/conviertete-en-agente", testid: "nav-agent" },
  { label: "Demo Agente",                    type: "route",  target: "/demo",     testid: "nav-demo" },
];

export const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      if (user.role === "shopper") navigate("/swipe");
      else if (user.role === "owner") navigate("/owner-dashboard");
      else if (user.role === "agent") navigate("/agent-dashboard");
      else if (user.role === "admin") navigate("/admin-dashboard");
    }
  }, [user, navigate]);

  const handleMenuClick = (item) => {
    setMobileMenuOpen(false);
    if (item.type === "scroll") {
      const el = document.querySelector(item.target);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(item.target);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-body overflow-x-hidden selection:bg-[#CCD500] selection:text-black">
      {/* Top Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <img src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png" alt="Findperty" className="h-9 brightness-0 invert" />
          </div>
          <nav className="hidden lg:flex items-center gap-7 text-sm text-slate-300">
            {MENU_ITEMS.map((item) => (
              <button key={item.label} onClick={() => handleMenuClick(item)} data-testid={item.testid} className="hover:text-[#CCD500] transition-colors">
                {item.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5 hidden sm:flex" onClick={() => navigate("/auth")} data-testid="nav-login-btn">
              Ingresar
            </Button>
            <Button className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all shadow-[0_0_20px_rgba(204,213,0,0.3)] rounded-full px-5" onClick={() => navigate("/auth")} data-testid="nav-cta-btn">
              Empezar
            </Button>
            <button className="lg:hidden text-white p-2" onClick={() => setMobileMenuOpen((v) => !v)} data-testid="nav-mobile-toggle" aria-label="Menú">
              {mobileMenuOpen ? <XCircle className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Mobile menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-white/10 bg-[#0B1120]/95 backdrop-blur-xl overflow-hidden">
              <nav className="flex flex-col px-6 py-3 gap-1 text-sm">
                {MENU_ITEMS.map((item) => (
                  <button key={item.label} onClick={() => handleMenuClick(item)} data-testid={`mobile-${item.testid}`}
                    className="text-left py-3 px-3 rounded-lg text-slate-200 hover:bg-white/5 hover:text-[#CCD500] transition-colors">
                    {item.label}
                  </button>
                ))}
                <button onClick={() => { setMobileMenuOpen(false); navigate("/auth"); }} className="text-left py-3 px-3 rounded-lg text-slate-200 hover:bg-white/5 hover:text-[#CCD500] transition-colors sm:hidden" data-testid="mobile-nav-login">
                  Ingresar
                </button>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-28 px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 opacity-30 pointer-events-none"
          style={{ backgroundImage: `url(${BG_ABSTRACT})`, backgroundSize: "cover", backgroundPosition: "center",
            maskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)",
            WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,1) 30%, rgba(0,0,0,0) 100%)" }} />
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] bg-[#CCD500]/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-40 -right-40 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex h-2 w-2 rounded-full bg-[#CCD500] animate-pulse" />
              <Eyebrow>Real Estate × Inteligencia Psicográfica</Eyebrow>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]" data-testid="hero-title">
              Encuentra tu{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-[#CCD500] via-[#E5FF00] to-[#CCD500] bg-clip-text text-transparent">propiedad ideal</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none">
                  <path d="M2 8C80 2 220 2 298 8" stroke="#CCD500" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>.
            </h1>
            <p className="mt-7 text-lg md:text-xl text-slate-400 leading-relaxed max-w-2xl">
              La única app inmobiliaria en México basada en <span className="text-white font-medium">matchmaking psicográfico</span>.
              52 puntos para encontrar la casa que se ajusta a tu vida.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={() => navigate("/haz-match")} data-testid="hero-primary-cta"
                className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 transition-all shadow-[0_0_30px_rgba(204,213,0,0.4)] rounded-full px-7 h-14 text-base">
                Haz Match con tu casa ideal
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/vende-tu-propiedad")} data-testid="hero-secondary-cta"
                className="bg-white/5 text-white border-white/20 hover:bg-white/10 hover:border-white/40 rounded-full px-7 h-14 text-base backdrop-blur-md">
                <Home className="w-4 h-4 mr-2" />
                Pon en venta tu Propiedad
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 items-center">
              <div className="flex -space-x-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 border-2 border-[#0B1120] flex items-center justify-center text-xs font-bold text-[#CCD500]">{String.fromCharCode(64 + i)}</div>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (<Star key={i} className="w-4 h-4 text-[#CCD500] fill-[#CCD500]" />))}
                <span className="text-sm text-slate-300 ml-2">4.9 · +500 usuarios activos</span>
              </div>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5">
            <SwipeDemo />
          </motion.div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative border-y border-white/5 bg-slate-950/40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { v: "52", k: "Puntos psicográficos" },
            { v: "30", k: "Master Partners" },
            { v: "10", k: "Regiones cubiertas" },
            { v: "70%+", k: "Match mínimo garantizado" },
          ].map((s) => (
            <div key={s.k} className="text-center md:text-left">
              <p className="font-display text-3xl md:text-4xl font-black text-white">{s.v}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">{s.k}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MASTER PARTNERS — COBERTURA NACIONAL */}
      <Section id="partners" className="bg-gradient-to-b from-transparent via-slate-950/40 to-transparent">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>Cobertura Nacional</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              Master Partners <span className="text-[#CCD500]">en todo México</span>.
            </h2>
            <p className="mt-4 text-slate-400 max-w-2xl">
              30 líderes locales en 10 regiones. Cada Master Partner cura propiedades premium y opera su red de agentes verificados en su ciudad.
            </p>
          </div>
          <Button onClick={() => navigate("/haz-match")} data-testid="mp-explore-all-btn"
            className="bg-[#CCD500] text-black font-bold hover:bg-[#E5FF00] hover:scale-105 rounded-full px-6 h-12 self-start md:self-end">
            Explorar los 30 MPs
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
        <MasterPartnersCarousel />
      </Section>

      {/* MATCHING PILLARS — La ciencia del Match */}
      <Section id="match">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Eyebrow>La ciencia del Match</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">
              52 puntos.<br />
              <span className="text-[#CCD500]">4 dimensiones</span>.<br />
              Un solo match real.
            </h2>
            <p className="mt-5 text-slate-400 max-w-md">
              No te mostramos propiedades aleatorias. Cada match nace de tu ADN de hogar — emoción, técnica, entorno y finanzas.
            </p>
            <Button onClick={() => navigate("/comprador")} className="mt-7 bg-white text-black hover:bg-[#CCD500] rounded-full px-6 transition-all" data-testid="match-cta-btn">
              Crear mi ADN de Hogar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {PILLARS.map((p, i) => (
              <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="group relative rounded-3xl bg-slate-900/60 backdrop-blur-md border border-white/10 p-6 hover:border-[#CCD500]/40 transition-all"
                data-testid={`pillar-${p.name.toLowerCase()}`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-11 h-11 rounded-2xl bg-[#CCD500]/10 border border-[#CCD500]/30 flex items-center justify-center">
                    <p.icon className="w-5 h-5 text-[#CCD500]" />
                  </div>
                  <span className="font-display text-3xl font-black text-white">{p.weight}<span className="text-[#CCD500]">%</span></span>
                </div>
                <h3 className="font-display text-xl font-bold text-white mb-1">{p.name}</h3>
                <p className="text-sm text-slate-400">{p.desc}</p>
                <div className="mt-5 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <motion.div initial={{ width: 0 }} whileInView={{ width: `${p.weight}%` }} viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.2 + i * 0.08 }} className="h-full bg-gradient-to-r from-[#CCD500] to-[#E5FF00]" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS — Proceso Simple */}
      <Section>
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Eyebrow>Proceso simple</Eyebrow>
          <h2 className="font-display text-4xl md:text-5xl font-bold mt-4 tracking-tight">Tres pasos para tu match.</h2>
        </div>
        <div className="relative grid md:grid-cols-3 gap-8">
          <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[#CCD500]/40 to-transparent" />
          {[
            { n: "01", t: "Crea tu perfil",       d: "52 preguntas para definir tu ADN de hogar. 5 minutos." },
            { n: "02", t: "Descubre tu match",    d: "Solo verás propiedades con 70%+ de compatibilidad real." },
            { n: "03", t: "Cierra con seguridad", d: "Contacto directo con agente verificado y firma protegida." },
          ].map((s, i) => (
            <motion.div key={s.n} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative text-center">
              <div className="relative mx-auto w-24 h-24 rounded-full bg-slate-900 border border-white/10 flex items-center justify-center mb-6 z-10">
                <span className="font-display text-2xl font-black text-[#CCD500]">{s.n}</span>
                <div className="absolute inset-0 rounded-full border border-[#CCD500]/30 animate-ping opacity-20" />
              </div>
              <h3 className="font-display text-xl font-bold text-white mb-2">{s.t}</h3>
              <p className="text-sm text-slate-400 max-w-xs mx-auto">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* CTA FINAL — Únete a Findperty */}
      <Section>
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#CCD500] to-[#9DAB00] p-10 md:p-16 text-center">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-display text-4xl md:text-6xl font-black text-black tracking-tighter mb-4 leading-[0.95]">
              Únete a Findperty
            </h2>
            <p className="text-black/70 text-lg max-w-xl mx-auto mb-8">
              La única app de matchmaking inmobiliario en México. 100% gratis para compradores.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" onClick={() => navigate("/comprador")} data-testid="final-cta-btn"
                className="bg-black text-[#CCD500] font-bold hover:bg-slate-900 hover:scale-105 rounded-full px-8 h-14 text-base">
                Crear cuenta gratis
                <Heart className="w-5 h-5 ml-2 fill-[#CCD500]" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/conviertete-en-agente")} data-testid="final-agent-btn"
                className="bg-transparent text-black border-black/40 hover:bg-black/5 rounded-full px-8 h-14 text-base">
                Soy Agente
              </Button>
            </div>
            <p className="text-xs text-black/60 mt-6">Sin tarjeta de crédito • Setup en 5 minutos</p>
          </div>
        </div>
      </Section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 px-6 lg:px-8 py-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <img src="https://customer-assets.emergentagent.com/job_propswipe-7/artifacts/65kacgp6_Isotipo%20Findperty-2.png" alt="Findperty" className="h-8 brightness-0 invert opacity-60" />
          <p className="text-sm text-slate-500">© 2026 Findperty. Todos los derechos reservados.</p>
          <div className="flex gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Términos</a>
            <a href="#" className="hover:text-white transition-colors">Contacto</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
